/* RYVENYOU depth layer.
   Three things live here:
     1. Scroll reveal  - [data-reveal] elements rise in as they enter view.
     2. Generic tilt   - [data-tilt] elements track the pointer in 3D.
     3. Specular sheen - [data-spec] elements get a highlight that follows
                         the pointer across the surface, so a panel reads as
                         lacquered rather than matte.

   Failure modes this guards against, because the cost of getting it wrong is
   a page of invisible content:
     - JS never loads          -> reveal styles are scoped to `html.js-depth`,
                                  which is only added here, so content renders
                                  normally.
     - IntersectionObserver
       never fires             -> a watchdog removes `js-depth` after 2.5s,
                                  making everything visible. Observed in a
                                  throttled/background renderer where neither
                                  rAF nor IO callbacks run at all.
     - Section re-render       -> `shopify:section:load` re-scans, otherwise
       (theme editor)             freshly injected nodes would never be
                                  observed and would stay at opacity 0. */
(function () {
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var observer = null;
  var observerFired = false;

  function revealAll() {
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
    });
  }

  /* ---------- 1. Scroll reveal ---------- */
  function observe(scope) {
    var targets = (scope || document).querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        observerFired = true;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          // Stagger siblings so a grid cascades instead of popping as one block.
          var group = el.parentElement
            ? el.parentElement.querySelectorAll(':scope > [data-reveal]')
            : [];
          var index = Array.prototype.indexOf.call(group, el);
          el.style.transitionDelay = (index > 0 ? Math.min(index, 6) * 70 : 0) + 'ms';
          el.classList.add('is-revealed');
          observer.unobserve(el);
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    }

    targets.forEach(function (el) {
      if (!el.classList.contains('is-revealed')) observer.observe(el);
    });
  }

  function initReveal() {
    // Opt in only once we know JS runs; see note above.
    document.documentElement.classList.add('js-depth');
    observe(document);

    // Watchdog: a healthy IntersectionObserver fires its first callback almost
    // immediately, even for elements that are not intersecting. If nothing has
    // fired by now the mechanism is broken - degrade to plain visible content
    // rather than leaving the page blank.
    window.setTimeout(function () {
      if (!observerFired) {
        document.documentElement.classList.remove('js-depth');
        revealAll();
      }
    }, 2500);
  }

  /* ---------- 2. Generic pointer tilt ---------- */
  function initTilt(scope) {
    if (reduceMotion) return;

    (scope || document).querySelectorAll('[data-tilt]').forEach(function (el) {
      if (el.dataset.tiltBound) return;
      el.dataset.tiltBound = '1';

      var maxY = parseFloat(el.getAttribute('data-tilt-y')) || 7;
      var maxX = parseFloat(el.getAttribute('data-tilt-x')) || 5;
      var targetX = 0, targetY = 0, curX = 0, curY = 0, frame = null;

      function step() {
        curX += (targetX - curX) * 0.10;
        curY += (targetY - curY) * 0.10;
        el.style.setProperty('--tilt-y', curX.toFixed(3) + 'deg');
        el.style.setProperty('--tilt-x', curY.toFixed(3) + 'deg');
        if (Math.abs(targetX - curX) > 0.01 || Math.abs(targetY - curY) > 0.01) {
          frame = requestAnimationFrame(step);
        } else {
          frame = null;
        }
      }
      function start() { if (frame === null) frame = requestAnimationFrame(step); }

      el.addEventListener('pointermove', function (event) {
        if (event.pointerType === 'touch') return;
        var rect = el.getBoundingClientRect();
        targetX = ((event.clientX - rect.left) / rect.width - 0.5) * 2 * maxY;
        targetY = -((event.clientY - rect.top) / rect.height - 0.5) * 2 * maxX;
        el.classList.add('is-tilting');
        start();
      });

      el.addEventListener('pointerleave', function () {
        targetX = 0;
        targetY = 0;
        el.classList.remove('is-tilting');
        start();
      });
    });
  }

  /* ---------- 3. Pointer specular ---------- */
  /* Writes position as percentages so the CSS gradient can consume them
     directly. Opacity is a separate property rather than folding it into the
     gradient, so the fade in/out can be transitioned while the position
     tracks instantly. */
  function initSpec(scope) {
    if (reduceMotion) return;

    (scope || document).querySelectorAll('[data-spec]').forEach(function (el) {
      if (el.dataset.specBound) return;
      el.dataset.specBound = '1';

      el.addEventListener('pointermove', function (event) {
        if (event.pointerType === 'touch') return;
        var rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        el.style.setProperty('--spec-x', (((event.clientX - rect.left) / rect.width) * 100).toFixed(1) + '%');
        el.style.setProperty('--spec-y', (((event.clientY - rect.top) / rect.height) * 100).toFixed(1) + '%');
        el.style.setProperty('--spec-o', '1');
      });

      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--spec-o', '0');
      });
    });
  }

  function init() {
    initReveal();
    initTilt(document);
    initSpec(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders a section's DOM wholesale; rebind the new nodes.
  document.addEventListener('shopify:section:load', function (event) {
    var scope = event.target || document;
    if (document.documentElement.classList.contains('js-depth')) {
      observe(scope);
    } else {
      scope.querySelectorAll('[data-reveal]').forEach(function (el) {
        el.classList.add('is-revealed');
      });
    }
    initTilt(scope);
    initSpec(scope);
  });
})();
